import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { ProtectedComponent } from '../../../../domain/components/rbac/ProtectedComponent';
import { CreateAdminUserData } from '../../../../domain/types/admin-user';
import { Role } from '../../../../domain/types/rbac';
import { User } from '../../../../domain/types/user';
import { AdminUserService } from '../../../../domain/use-cases/admin-user/admin-user-service';
import { RoleService } from '../../../../domain/use-cases/rbac/role-service';
import Button from '../../../components/button';
import { Input } from '../../../components/inputs/input';

// Esquema de validación base
const adminUserSchema = z.object({
  email: z.string().email('Email inválido'),
  name: z.string().min(2, 'El nombre debe tener al menos 2 caracteres'),
  lastname: z.string().min(2, 'El apellido debe tener al menos 2 caracteres'),
  roles: z.array(z.string()).min(1, 'Debe seleccionar al menos un rol'),
  isEnabled: z.boolean().default(true),
  document: z.string().optional(),
});

type AdminUserFormData = z.infer<typeof adminUserSchema>;

// Componente para Input con etiqueta y error (definido fuera para evitar re-creación)
const FormField = ({
  label,
  value,
  onChange,
  error,
  type = 'text',
  placeholder,
  required = false,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  type?: string;
  placeholder?: string;
  required?: boolean;
}) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <Input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
    />
    {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
  </div>
);

export function AddAdminUserPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<AdminUserFormData>({
    email: '',
    name: '',
    lastname: '',
    roles: [],
    isEnabled: true,
    document: '',
  });

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Estados para verificación de usuario existente
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [existingUser, setExistingUser] = useState<User | null>(null);
  const [emailChecked, setEmailChecked] = useState(false);

  const adminUserService = React.useMemo(() => new AdminUserService(), []);
  const roleService = React.useMemo(() => new RoleService(), []);

  const loadRoles = useCallback(async () => {
    try {
      setLoadingRoles(true);
      const rolesData = await roleService.getAllRoles();
      setRoles(rolesData || []);
    } catch (error) {
      console.error('Error loading roles:', error);
    } finally {
      setLoadingRoles(false);
    }
  }, [roleService]);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  // Función para verificar si el email existe en la colección users
  const checkEmailInUsers = useCallback(
    async (email: string) => {
      if (!email || !email.includes('@')) {
        setExistingUser(null);
        setEmailChecked(false);
        return;
      }

      setCheckingEmail(true);
      try {
        const user = await adminUserService.checkUserInUsersCollection(email);
        setExistingUser(user);
        setEmailChecked(true);

        if (user) {
          // Autocompletar campos con datos del usuario existente
          setFormData((prev) => ({
            ...prev,
            name: user.name || '',
            lastname: user.lastname || '',
            document: user.document || '',
          }));
          toast.info('Usuario encontrado en el sistema', { theme: 'colored' });
        }
      } catch (error) {
        console.error('Error verificando email:', error);
      } finally {
        setCheckingEmail(false);
      }
    },
    [adminUserService],
  );

  const handleInputChange = (field: keyof AdminUserFormData, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Si cambia el email, resetear el estado de verificación
    if (field === 'email') {
      setExistingUser(null);
      setEmailChecked(false);
    }

    // Limpiar error del campo si existe
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleRoleChange = (roleId: string, checked: boolean) => {
    setFormData((prev) => ({
      ...prev,
      roles: checked
        ? [...prev.roles, roleId]
        : prev.roles.filter((id) => id !== roleId),
    }));

    // Limpiar error de roles si existe
    if (errors.roles) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors.roles;
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    try {
      adminUserSchema.parse(formData);

      // Validación adicional: documento requerido si usuario es nuevo
      if (
        !existingUser &&
        (!formData.document || formData.document.length < 6)
      ) {
        setErrors((prev) => ({
          ...prev,
          document:
            'El documento es requerido (mínimo 6 caracteres) para usuarios nuevos',
        }));
        return false;
      }

      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Verificar que se haya comprobado el email
    if (!emailChecked) {
      toast.warning('Por favor, verifique el email antes de continuar', {
        theme: 'colored',
      });
      await checkEmailInUsers(formData.email);
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const createData: CreateAdminUserData = {
        email: formData.email,
        name: formData.name,
        lastname: formData.lastname,
        roles: formData.roles,
        isEnabled: formData.isEnabled,
        document: formData.document,
        existingUserId: existingUser?.id,
      };

      const result = await adminUserService.createAdminUser(createData);

      if (result.success) {
        toast.success(
          result.message || 'Usuario administrativo creado exitosamente',
          {
            theme: 'colored',
          },
        );
        navigate('/dashboard/admin-users');
      } else {
        toast.error(`Error al crear usuario: ${result.message}`, {
          theme: 'colored',
        });
      }
    } catch (error) {
      console.error('Error creating admin user:', error);
      toast.error('Error al crear el usuario administrativo', {
        theme: 'colored',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <ProtectedComponent
      resource="admin_users"
      action="create"
      fallback={
        <div className="flex justify-center items-center min-h-96">
          <div className="text-center">
            <h2 className="text-lg font-medium text-gray-900">
              Acceso Denegado
            </h2>
            <p className="text-gray-600 mt-1">
              No tienes permisos para crear usuarios
            </p>
          </div>
        </div>
      }
    >
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Agregar Usuario Administrativo
            </h1>
            <p className="text-gray-600 mt-1">
              Crea un nuevo usuario con acceso al panel de administración
            </p>
          </div>
          <Button
            label="Volver"
            variant="secondary"
            onClick={() => navigate('/dashboard/admin-users')}
          />
        </div>

        {/* Form */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">
              Información del Usuario
            </h3>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Información Básica */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Campo Email con verificación automática onBlur */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    onBlur={() => {
                      if (formData.email.includes('@')) {
                        checkEmailInUsers(formData.email);
                      }
                    }}
                    placeholder="usuario@ejemplo.com"
                    className={`w-full ${errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
                  />
                  {checkingEmail && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                    </div>
                  )}
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>

              {/* Campo Documento */}
              {emailChecked && !existingUser ? (
                <FormField
                  label="Documento"
                  type="text"
                  value={formData.document || ''}
                  onChange={(value) => handleInputChange('document', value)}
                  error={errors.document}
                  placeholder="Número de documento"
                  required
                />
              ) : emailChecked && existingUser?.document ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Documento
                  </label>
                  <Input
                    type="text"
                    value={existingUser.document}
                    onChange={() => {}}
                    disabled
                    className="w-full bg-gray-100"
                  />
                </div>
              ) : (
                <div></div>
              )}

              {/* Campo Nombre */}
              <FormField
                label="Nombre"
                type="text"
                value={formData.name}
                onChange={(value) => handleInputChange('name', value)}
                error={errors.name}
                placeholder="Juan"
                required
              />

              {/* Campo Apellido */}
              <FormField
                label="Apellido"
                type="text"
                value={formData.lastname}
                onChange={(value) => handleInputChange('lastname', value)}
                error={errors.lastname}
                placeholder="Pérez"
                required
              />
            </div>

            {/* Indicador de estado del usuario */}
            {emailChecked && (
              <div
                className={`p-3 rounded-md text-sm ${
                  existingUser
                    ? 'bg-green-50 text-green-700 border border-green-200'
                    : 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                }`}
              >
                {existingUser ? (
                  <>
                    ✅ Usuario encontrado en el sistema:{' '}
                    <strong>
                      {existingUser.name} {existingUser.lastname}
                    </strong>
                    . Los datos se han autocompletado.
                  </>
                ) : (
                  <>
                    ⚠️ Usuario nuevo - se creará cuenta en el sistema. La
                    contraseña inicial será el número de documento.
                  </>
                )}
              </div>
            )}

            {/* Roles */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Roles <span className="text-red-500">*</span>{' '}
                {loadingRoles && (
                  <span className="text-gray-500">(Cargando...)</span>
                )}
              </label>
              {errors.roles && (
                <p className="text-sm text-red-600 mb-2">{errors.roles}</p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {roles.map((role) => (
                  <label
                    key={role.id}
                    className="flex items-center p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={
                        role.id ? formData.roles.includes(role.id) : false
                      }
                      onChange={(e) =>
                        role.id && handleRoleChange(role.id, e.target.checked)
                      }
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {role.name}
                      </div>
                      {role.description && (
                        <div className="text-xs text-gray-500">
                          {role.description}
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Estado */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isEnabled"
                checked={formData.isEnabled}
                onChange={(e) =>
                  handleInputChange('isEnabled', e.target.checked)
                }
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="isEnabled" className="ml-2 text-sm text-gray-700">
                Usuario activo
              </label>
            </div>

            {/* Botones */}
            <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
              <Button
                label="Cancelar"
                variant="secondary"
                onClick={() => navigate('/dashboard/admin-users')}
                disabled={loading}
              />
              <Button
                label={loading ? 'Creando...' : 'Crear Usuario'}
                type="submit"
                disabled={loading}
                isLoading={loading}
              />
            </div>
          </form>
        </div>
      </div>
    </ProtectedComponent>
  );
}
